import type { TourClaim, TourEvidenceSource, TourEvidenceStrength, TourFixture } from '../data/tour-data';

export interface TourResolvedClaim {
  claim: TourClaim;
  source: TourEvidenceSource;
}

export function resolveTourClaims(fixture: TourFixture, claimIds: string[]): TourResolvedClaim[] {
  const wanted = new Set(claimIds);
  const resolved: TourResolvedClaim[] = [];
  for (const source of fixture.evidence_sources) {
    for (const claim of source.claims) {
      if (wanted.has(claim.claim_id)) resolved.push({ claim, source });
    }
  }
  return resolved;
}

export function TourEvidenceStrengthBadge({ strength }: { strength: TourEvidenceStrength }) {
  return <span className={`r42-tour-strength r42-tour-strength--${strength.toLowerCase()}`}>{strengthLabel(strength)}</span>;
}

export function TourEvidencePanel({
  fixture,
  claimIds,
  title = 'Evidence behind this',
  emptyText = 'This item does not cite evidence.',
}: {
  fixture: TourFixture;
  claimIds: string[];
  title?: string;
  emptyText?: string;
}) {
  const claims = resolveTourClaims(fixture, claimIds);
  const sourceCount = new Set(claims.map((item) => item.source.document_id)).size;

  return (
    <aside className="r42-tour-evidence-panel" aria-label={title}>
      <div className="r42-tour-evidence-panel__head">
        <div>
          <span className="r42-eyebrow">Evidence</span>
          <h2>{title}</h2>
        </div>
        {claims.length ? <span>{claims.length} {claims.length === 1 ? 'claim' : 'claims'} · {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}</span> : null}
      </div>

      {claims.length ? (
        <div className="r42-tour-evidence-list">
          {claims.map(({ claim, source }) => (
            <article key={`${source.document_id}:${claim.claim_id}`} className="r42-tour-evidence-item">
              <div className="r42-tour-evidence-item__meta">
                <span className="r42-tour-source-type">
                  <i className={`bi ${iconForEvidence(source.evidence_type)}`} aria-hidden="true" />
                  {source.evidence_type}
                </span>
                <TourEvidenceStrengthBadge strength={claim.evidence_strength} />
              </div>
              <strong>{source.title}</strong>
              <p>{claim.statement}</p>
              <a href={source.asset_path} target="_blank" rel="noopener noreferrer">Open source <i className="bi bi-box-arrow-up-right" aria-hidden="true" /></a>
            </article>
          ))}
        </div>
      ) : (
        <p className="r42-tour-evidence-empty">{emptyText}</p>
      )}
    </aside>
  );
}

export function iconForEvidence(type: TourEvidenceSource['evidence_type']): string {
  if (type === 'IMAGE') return 'bi-image';
  if (type === 'CSV') return 'bi-table';
  return 'bi-file-earmark-pdf';
}

function strengthLabel(strength: TourEvidenceStrength): string {
  if (strength === 'STRONG') return 'Strong evidence';
  if (strength === 'MODERATE') return 'Moderate evidence';
  return 'Limited evidence';
}
